import React from 'react';
import { Box, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { MUITableColumn } from 'components/shared/MUITable';
import { AssistantLogRow } from './types';

const PROCESS_TYPE_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  email_ingestion: { label: 'Magic Email Parsing', bg: '#f3e5f5', color: '#9c27b0' },
  doc_parsing: { label: 'Document Parsing', bg: '#e8eaf6', color: '#3f51b5' },
  subprime_agent: { label: 'Qualification Assistant', bg: '#e0f2f1', color: '#00897b' },
};

const OUTCOME_CONFIG: Record<string, { label: string; color: string; icon: React.ReactElement }> = {
  success: { label: 'Success', color: '#66bb6a', icon: <CheckCircleIcon sx={{ fontSize: 18, color: '#66bb6a' }} /> },
  failed: { label: 'Failed', color: '#ff5252', icon: <ErrorIcon sx={{ fontSize: 18, color: '#ff5252' }} /> },
};

export const createTableColumns = (
  onViewDetails: (row: AssistantLogRow) => void
): MUITableColumn<AssistantLogRow>[] => [
  {
    header: 'Date & Time',
    renderKey: 'createdDateTime',
    cellProps: { sx: { minWidth: 180, width: 180 } },
  },
  {
    header: 'Process Type',
    renderKey: 'processType',
    cellProps: { sx: { minWidth: 200, width: 200 } },
    renderCell: row => {
      const config = PROCESS_TYPE_CONFIG[row.processType];
      if (!config) return <Typography variant="body2">{row.processType}</Typography>;
      return (
        <Box
          sx={{
            display: 'inline-flex',
            px: 0.75,
            borderRadius: '2px',
            bgcolor: config.bg,
          }}
        >
          <Typography variant="body2" sx={{ color: config.color, whiteSpace: 'nowrap' }}>
            {config.label}
          </Typography>
        </Box>
      );
    },
  },
  {
    header: 'Trigger',
    renderKey: 'triggerDescription',
    cellProps: { sx: { flex: 1 } },
  },
  {
    header: 'Attachment',
    renderKey: 'attachmentName',
    cellProps: { sx: { minWidth: 260, width: 260 } },
    renderCell: row => {
      if (!row.attachmentName) return null;
      const overflow = (row.attachmentCount as number) - 1;
      return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ overflow: 'hidden' }}>
          <CloudDownloadIcon sx={{ fontSize: 20, flexShrink: 0, color: 'text.secondary' }} />
          <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {row.attachmentName}
          </Typography>
          {overflow > 0 && (
            <Typography variant="caption" sx={{ flexShrink: 0, color: 'text.secondary' }}>
              +{overflow}
            </Typography>
          )}
        </Stack>
      );
    },
  },
  {
    header: 'Matched Record',
    renderKey: 'matchedRecord',
    cellProps: { sx: { minWidth: 260, width: 260 } },
    renderCell: row => {
      if (!row.matchedRecord) {
        return (
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            Record Not Found
          </Typography>
        );
      }
      if (row.matchedRecordUrl) {
        return (
          <Link href={row.matchedRecordUrl} target="_blank" rel="noopener noreferrer" variant="body2">
            {row.matchedRecord}
          </Link>
        );
      }
      return <Typography variant="body2">{row.matchedRecord}</Typography>;
    },
  },
  {
    header: 'Outcome',
    renderKey: 'outcome',
    cellProps: { sx: { minWidth: 200, width: 200 } },
    renderCell: row => {
      if (!row.outcome) return null;
      const config = OUTCOME_CONFIG[row.outcome];
      if (!config) return <Typography variant="body2">{row.outcome}</Typography>;
      return (
        <Stack direction="row" spacing={0.25} alignItems="center">
          {config.icon}
          <Typography variant="subtitle2" sx={{ color: config.color, whiteSpace: 'nowrap' }}>
            {config.label}
          </Typography>
        </Stack>
      );
    },
  },
  {
    header: 'Action',
    renderKey: 'id',
    cellProps: { sx: { minWidth: 66, width: 66 } },
    renderCell: row => (
      <Tooltip title="View details">
        <IconButton size="small" onClick={() => onViewDetails(row)}>
          <VisibilityIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Tooltip>
    ),
  },
];
